import type { components } from '@fluencelabs/vodopad-admin-api'

import client from '@/api/client'

type VmConfigurationDto = components['schemas']['VmConfigurationDto']
type AdminPriceInfoDto = components['schemas']['AdminPriceInfoDto']

export type PriceResourceType = 'vm' | 'storage' | 'public-ip'

export const HOURS_PER_MONTH = 730
export const SECONDS_PER_HOUR = 3600

export const PRICE_SCHEDULE_URLS: Record<PriceResourceType, string> = {
  vm: '/v1/admin/prices/vm/schedule',
  storage: '/v1/admin/prices/storage/schedule',
  'public-ip': '/v1/admin/prices/public-ip/schedule',
}

export const PRICE_ACTIVE_URLS: Record<PriceResourceType, string> = {
  vm: '/v1/admin/prices/vm/active',
  storage: '/v1/admin/prices/storage/active',
  'public-ip': '/v1/admin/prices/public-ip/active',
}

export async function postPriceUpdate(
  resourceType: PriceResourceType,
  body: Record<string, unknown>,
) {
  if (resourceType === 'vm') {
    await client.POST('/v1/admin/prices/vm', { body })
  } else if (resourceType === 'storage') {
    await client.POST('/v1/admin/prices/storage', { body })
  } else {
    await client.POST('/v1/admin/prices/public-ip', { body })
  }
}

export function monthlyFromHourly(hourly: string) {
  return (Number(hourly) * HOURS_PER_MONTH).toFixed(2)
}

export function stripTrailingZeroes(s: string) {
  if (!s.includes('.')) return s
  return s.replace(/\.?0+$/, '')
}

export function formatPriceTriple(hourly: string) {
  const h = Number(hourly)
  const perSecond = stripTrailingZeroes((h / SECONDS_PER_HOUR).toPrecision(6))
  const perHour = stripTrailingZeroes(hourly)
  const perMonth = stripTrailingZeroes((h * HOURS_PER_MONTH).toFixed(2))
  return { perSecond, perHour, perMonth }
}

export function formatVmConfig(cfg: VmConfigurationDto) {
  return `${cfg.name} (${cfg.vcpu}vCPU, ${cfg.ramGb}GB)`
}

export interface PriceEntry {
  id: string
  pricePerHourPerQty: string
  activeSince: string
  activeUntil: string | null | undefined
  createdAt: string
  expired: boolean
}

export function isExpired(p: AdminPriceInfoDto): boolean {
  return p.activeUntil != null && new Date(p.activeUntil) < new Date()
}

export function toPriceEntry(p: AdminPriceInfoDto): PriceEntry {
  return {
    id: p.id,
    pricePerHourPerQty: p.pricePerHourPerQty,
    activeSince: p.activeSince,
    activeUntil: p.activeUntil,
    createdAt: p.createdAt,
    expired: isExpired(p),
  }
}
